// Calofay i18n — lightweight custom implementation to avoid dependency bloat.
// Arabic is the default and is statically imported so first paint has no
// blank strings. English is lazy-loaded via dynamic import the moment a
// user picks it in Settings → Language.
//
// Why lazy-load? On low-end Android devices, the full EN dictionary +
// landing_en copy pushed the JS heap high enough that the OS killed the
// external camera intent (see WeightLogModal). Keeping EN off the initial
// bundle frees ~65KB and unblocks camera capture for Arabic users, who
// make up the vast majority of Calofay's audience.
import { createContext, useContext, useEffect, useState } from "react";
import AR from "./i18n/ar-dict";
import { AuthContext } from "./auth";

const STORAGE_KEY = "calofay:lang";

// Cache dicts once resolved so switching AR ↔ EN doesn't refetch the chunk.
const dictCache = { ar: AR };
let inflight = null;

function loadDict(lang) {
  if (dictCache[lang]) return Promise.resolve(dictCache[lang]);
  if (lang === "en") {
    if (!inflight) {
      // webpackChunkName lets us confirm the split in build output
      inflight = import(/* webpackChunkName: "i18n-en" */ "./i18n/en-dict")
        .then((mod) => { dictCache.en = mod.default; inflight = null; return mod.default; });
    }
    return inflight;
  }
  // Unknown language → fall back to AR (already cached).
  return Promise.resolve(AR);
}

const I18nContext = createContext({
  lang: "ar",
  setLang: () => {},
  t: (k) => k,
});

export function I18nProvider({ children }) {
  // Detect coach-share view once at mount — we branch on this to skip
  // any backend writes that would mutate the TRAINEE'S profile when
  // the coach flips the switcher. The coach's language pick lives
  // only in localStorage (and clears when the share session ends).
  const isSharedView = typeof window !== "undefined"
    && /\/share\/progress\//.test(window.location.pathname);

  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "ar";
    } catch {
      return "ar";
    }
  });
  // Active dict — starts on whatever's cached for the current lang. For
  // Arabic (the default) that's the sync-imported AR. For English on
  // first load the dict resolves shortly after mount via the effect below.
  const [dict, setDict] = useState(() => dictCache[lang] || AR);

  // ── Sync lang from the authenticated user's `preferred_language` ────
  // Server-side `_apply_lang` (recipes) and the LLM meal/coach prompts
  // ALL branch on `user.preferred_language`. Frontend `lang` used to
  // only read from localStorage — so a fresh device with an EN-preference
  // account showed Arabic chrome + English recipe titles until the user
  // toggled the language switch (which finally pushed both sides into
  // agreement). We now bootstrap `lang` from the authed user on every
  // login/refresh, so backend and frontend are locked in step from the
  // very first paint.
  const authCtx = useContext(AuthContext);
  useEffect(() => {
    // In the coach-share view the "authed" user object is actually the
    // TRAINEE (resolved from the share token). Auto-syncing lang from
    // there would force the coach to view the page in the trainee's
    // language — the exact opposite of letting the coach pick. Skip.
    if (isSharedView) return;
    const backendLang = authCtx?.user?.preferred_language;
    if (!backendLang) return;
    if (backendLang !== "ar" && backendLang !== "en") return;
    if (backendLang === lang) return;
    setLangState(backendLang);
    try { localStorage.setItem(STORAGE_KEY, backendLang); } catch {}
  }, [authCtx?.user?.preferred_language]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    loadDict(lang).then((d) => { if (!cancelled) setDict(d); });
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    return () => { cancelled = true; };
  }, [lang]);

  const setLang = (newLang) => {
    if (newLang !== "ar" && newLang !== "en") return;
    setLangState(newLang);
    try { localStorage.setItem(STORAGE_KEY, newLang); } catch {}
    // Coach-share view: the JWT resolves to the TRAINEE — patching
    // /user/profile here would silently overwrite the trainee's
    // saved language and re-target their scheduled push reminders to
    // the coach's pick. Skip both side-effects entirely.
    if (isSharedView) return;
    // Persist to the user's profile so server-driven translations
    // (recipes _apply_lang, notifications, etc.) match the UI. This
    // is fire-and-forget — a network hiccup shouldn't block the UI
    // switch, and the localStorage value covers offline reads.
    import("./api").then(({ default: api }) => {
      api.patch("/user/profile", { language: newLang }).catch(() => {});
    }).catch(() => {});
    // Push subscription language (scheduled reminders).
    import("./push").then((m) => {
      try { m.syncPushLanguage(newLang); } catch (_) {}
    }).catch(() => {});
  };

  // Fall through: active dict → Arabic (always available) → key itself.
  const t = (key, fallback) => dict?.[key] ?? fallback ?? AR[key] ?? key;

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
